import type { RuleDef } from '../core/catalog';
import { RULES } from '../core/catalog';
import type { RuleId } from '../core/constants';

/** Pure helpers for physics rule application — unit-testable without Phaser */
export function getRule(ruleId: RuleId): RuleDef {
  return RULES[ruleId] ?? RULES.normal;
}

export function gravityScaleForRule(ruleId: RuleId): number {
  return getRule(ruleId).gravityY;
}

export function windForceForRule(ruleId: RuleId): number {
  return getRule(ruleId).windX;
}

export function restitutionForRule(ruleId: RuleId): { world: number; bot: number } {
  const r = getRule(ruleId);
  return { world: r.worldRestitution, bot: r.botRestitution };
}

/** Distance from spawn origin along +X (world units) */
export function measureDistance(botX: number, originX: number): number {
  return Math.max(0, botX - originX);
}

export function crossedGoal(botX: number, goalX: number, radius = 20): boolean {
  return botX + radius >= goalX;
}

export interface RunOutcome {
  distance: number;
  success: boolean;
  delta: number;
  improved: boolean;
}

export function evaluateRun(
  botX: number,
  originX: number,
  goalX: number,
  previousBest: number,
): RunOutcome {
  const distance = measureDistance(botX, originX);
  const success = crossedGoal(botX, goalX);
  const delta = distance - previousBest;
  return {
    distance,
    success,
    delta,
    improved: delta > 0.5,
  };
}

/** Whether a second-experiment activation should fire */
export function shouldMarkSecondExperiment(opts: {
  runIndexAfter: number;
  hasChangedVariable: boolean;
  alreadyMarked: boolean;
}): boolean {
  if (opts.alreadyMarked) return false;
  if (opts.runIndexAfter < 2) return false;
  return opts.hasChangedVariable;
}

export type VariableChangeKind = 'rule' | 'tool_layout' | 'tool_count' | 'none';

export function detectVariableChange(
  before: { rule: RuleId; toolSignature: string },
  after: { rule: RuleId; toolSignature: string },
): VariableChangeKind {
  if (before.rule !== after.rule) return 'rule';
  if (before.toolSignature !== after.toolSignature) {
    const ba = before.toolSignature.split('|').filter(Boolean);
    const aa = after.toolSignature.split('|').filter(Boolean);
    if (ba.length !== aa.length) return 'tool_count';
    return 'tool_layout';
  }
  return 'none';
}

export function toolSignature(
  tools: { toolId: string; x: number; y: number; angle: number }[],
): string {
  return tools
    .map((t) => `${t.toolId}:${Math.round(t.x)}:${Math.round(t.y)}:${Math.round(t.angle)}`)
    .sort()
    .join('|');
}

/** Impulse / force constants used by tools (shared with scene) */
export const TOOL_PHYSICS = {
  springImpulseY: -0.12,
  springImpulseX: 0.04,
  fanForce: 0.0011,
  fanRadius: 120,
  balloonForce: -0.00085,
  magnetForce: 0.0014,
  magnetRadius: 160,
  bouncePadRestitution: 1.35,
  rocketImpulse: -0.09,
  rocketDurationMs: 900,
  portalBoost: 0.08,
  bumperImpulse: 0.06,
} as const;

export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function worldFloorY(height: number): number {
  return height - 90;
}
